import { DistrictDocumentsService } from '@/service';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DataTableComponent, FilePreviewComponent } from '@/components';
import { Subject, takeUntil } from 'rxjs';
import { DataTableColumn, DistrictDocument } from '@/interfaces';
import { AuthService } from '@/auth/services';
import { DataTableColumnType, DistrictDocumentType, FilterType } from '@/enums';
import { ActivatedRoute } from '@angular/router';
import { applyDistrictAndCompetitorToFilterObject, applyDistrictAndCompetitorToQuery } from '@/helpers';

@Component({
    selector: 'app-district-document-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    imports: [DataTableComponent]
})
export class DistrictDocumentListingComponent implements OnChanges, OnDestroy {
    @Input() type: DistrictDocumentType = DistrictDocumentType.AGENDA;

    reloadTable$ = new Subject<void>();
    destroy$ = new Subject<void>();
    districtMappingId?: string;
    columns: DataTableColumn<DistrictDocument>[] = [
        {
            field: 'source_file',
            header: 'Document',
            type: DataTableColumnType.MODAL,
            modalComponent: FilePreviewComponent,
            modalTitle: 'View File',
            modalData: (row: DistrictDocument) => ({ url: row.source_file_url }),
            modalBtnDisabled: (row: DistrictDocument) => !row.source_file_url
        },
        {
            field: 'year',
            header: 'Year',
            filterType: FilterType.NUMERIC
        },
        {
            field: 'competitor.competitor',
            header: 'Agency Name',
            type: DataTableColumnType.LINK,
            link: (row: DistrictDocument) => (row.competitor_id ? `/competitor-mappings/${row.competitor_id}` : null)
        },
        {
            field: 'competitor.agency_type',
            header: 'Agency Type'
        },
        {
            field: 'date',
            header: 'Date',
            type: DataTableColumnType.DATE,
            filterType: FilterType.DATE
        }
    ];

    constructor(
        public authService: AuthService,
        private service: DistrictDocumentsService,
        private route: ActivatedRoute
    ) {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const id = this.route.snapshot.paramMap.get('district_mapping_id');
            if (id !== this.districtMappingId) {
                this.districtMappingId = id ?? undefined;
                this.reloadTable$.next();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['type'] && !changes['type'].firstChange) {
            queueMicrotask(() => this.reloadTable$.next());
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadData = (params: Record<string, any>) =>
        this.service.listing(
            applyDistrictAndCompetitorToQuery(params, {
                type: this.type,
                districtMappingId: this.districtMappingId
            })
        );

    exportData = (params: Record<string, any>) =>
        this.service.export(
            applyDistrictAndCompetitorToFilterObject(params, {
                type: this.type,
                districtMappingId: this.districtMappingId
            })
        );
}
